import { Component, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcCorporationListItemDto } from 'webcommon/shared';

import { FcItemModalContext } from '../../../models';
import { FcItemListBaseComponent, FormGroupExtended } from '../../generic';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';

interface PhoneFormGroup {
  phoneNumber: FormControl;
  phoneType: FormControl;
  phoneTypeUid: FormControl;
}

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-corporation-list',
  templateUrl: './fc-corporation-list.component.html',
})
export class FcCorporationListComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: FcCorporationListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;
  @Input() emailControlsEnabled = true;

  // Form Controls
  public externalIdControl: FormControl = new FormControl<string>('');
  public nameControl: FormControl = new FormControl<string>('');
  public primaryContactControl: FormControl = new FormControl<string>('');
  public secondaryContactControl: FormControl = new FormControl<string>('');
  public emailOneControl: FormControl = new FormControl<string>('');
  public emailTwoControl: FormControl = new FormControl<string>('');

  // Form Groups
  public addressGroup = new FormGroupExtended({});
  public phoneOneGroup = new FormGroup<Partial<PhoneFormGroup>>({});
  public phoneTwoGroup = new FormGroup<Partial<PhoneFormGroup>>({});
  public phoneThreeGroup = new FormGroup<Partial<PhoneFormGroup>>({});
  public phoneFourGroup = new FormGroup<Partial<PhoneFormGroup>>({});

  constructor(injector: Injector) {
    super(injector);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, {
        ExternalID: {
          MaxLength: 25,
        },
        Name: {
          IsRequired: true,
          MaxLength: 100,
        },
        Contact: {
          MaxLength: 50,
        },
        Email: {
          MaxLength: 100,
        },
      });
    }

    if (changes.form != null) {
      this.addControlsToForm();
    }

    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  private addControlsToForm(): void {
    this.form.addControl('externalId', this.externalIdControl);
    this.form.addControl('name', this.nameControl);
    this.form.addControl('primaryContact', this.primaryContactControl);
    this.form.addControl('secondaryContact', this.secondaryContactControl);
    this.form.addControl('address', this.addressGroup);
    this.form.addControl('phoneOne', this.phoneOneGroup);
    this.form.addControl('phoneTwo', this.phoneTwoGroup);
    this.form.addControl('phoneThree', this.phoneThreeGroup);
    this.form.addControl('phoneFour', this.phoneFourGroup);
    if (this.emailControlsEnabled) {
      this.form.addControl('emailOne', this.emailOneControl);
      this.form.addControl('emailTwo', this.emailTwoControl);
    }
  }

  public setForm(): void {
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem?.ExternalID);

    // Name Control
    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem?.Name);

    // Primary Contact Control
    this.primaryContactControl.setValidators(this.fcListValidation.getValidators('PrimaryContact'));
    this.primaryContactControl.setValue(this.listItem?.PrimaryContact);

    // Secondary Contact Control
    this.secondaryContactControl.setValidators(this.fcListValidation.getValidators('SecondaryContact'));
    this.secondaryContactControl.setValue(this.listItem?.SecondaryContact);

    // Email One Control
    this.emailOneControl.setValidators(this.fcListValidation.getValidators('Email1'));
    this.emailOneControl.setValue(this.listItem?.Email1);

    // Email Two Control
    this.emailTwoControl.setValidators(this.fcListValidation.getValidators('Email2'));
    this.emailTwoControl.setValue(this.listItem?.Email2);
  }

  public onSetAreaCode(areaCode: string): void {
    const phoneNumberControl = this.phoneOneGroup.get('phoneNumber');
    if (phoneNumberControl != null && !phoneNumberControl.value) {
      phoneNumberControl.patchValue(areaCode);
      phoneNumberControl.markAsDirty();
    }
  }
}
